import React, { useEffect, useState } from "react";
import {
  UncontrolledBoard,
  KanbanCard as LibKanbanCard,
  KanbanColumn as LibKanbanColumn,
} from "@anythink-ltd/react-kanban";
import "@anythink-ltd/react-kanban/dist/styles.css";
import { IEntity } from "@/types/breeze/Entity";
import { IAny, PaginatedResult } from "@/types/breeze/Any";
import {
  StandardFieldDisplayType,
  FieldDisplayType,
  FileFieldDisplayType,
  RelationshipFieldDisplayType,
  UserFieldDisplayType,
} from "@/types/breeze/Field";
import { anyService } from "@/services/breeze/AnyService";
import { useQueryClient } from "@tanstack/react-query";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import { formatDate } from "@/utils/date";
import { useAuth } from "@/providers/AuthProvider";

const STORAGE_KEY = "entity-status-field-selections";

interface StatusFieldSelections {
  [key: string]: {
    // tenantId
    [key: string]: string; // entityName: selectedStatusField
  };
}

const getStoredStatusField = (
  entityName: string,
  tenantId: number | null,
): string => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const selections: StatusFieldSelections = stored ? JSON.parse(stored) : {};
    const tenantKey = String(tenantId || "default");
    return selections[tenantKey]?.[entityName] || "";
  } catch (error) {
    console.error("Error loading status field selection:", error);
    return "";
  }
};

const saveStatusFieldForEntity = (
  entityName: string,
  tenantId: number | null,
  field: string,
) => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const selections: StatusFieldSelections = stored ? JSON.parse(stored) : {};
    const tenantKey = String(tenantId || "default");

    selections[tenantKey] = selections[tenantKey] || {};
    selections[tenantKey][entityName] = field;

    localStorage.setItem(STORAGE_KEY, JSON.stringify(selections));
  } catch (error) {
    console.error("Error saving status field selection:", error);
  }
};

// Helper function to format field values based on display type
const formatFieldValue = (
  value: any,
  displayType?: FieldDisplayType,
): string => {
  if (value === null || value === undefined) return "None";

  switch (displayType) {
    case StandardFieldDisplayType.CHECKBOX:
    case StandardFieldDisplayType.RADIO:
      return value ? "Yes" : "No";
    case StandardFieldDisplayType.TIMESTAMP:
      return formatDate(value);
    case StandardFieldDisplayType.JSON:
      return typeof value === "object" ? JSON.stringify(value) : String(value);
    case FileFieldDisplayType.FILE:
      const files = value?.__data || [];
      return `${files.length} file(s)`;
    case UserFieldDisplayType.USER:
      if (!value || (!value.first_name && !value.last_name)) return "None";
      return `${value.first_name} ${value.last_name}`.trim();
    case RelationshipFieldDisplayType.RELATIONSHIP:
      if (Array.isArray(value)) {
        return `${value.length} item${value.length === 1 ? "" : "s"}`;
      }
      if (typeof value === "object" && value.__count !== undefined) {
        return `${value.__count} item${value.__count === 1 ? "" : "s"}`;
      }
      return String(value);
    default:
      return typeof value === "object" ? JSON.stringify(value) : String(value);
  }
};

// Fields to hide in the card display
const HIDDEN_FIELDS = [
  "tenant_id",
  "id",
  "title",
  "description",
  "locked",
  "entityName",
  "column_id",
  "position",
  "created_at",
  "updated_at",
];

const shouldShowField = (key: string, value: any, fieldDef?: any) => {
  // Skip hidden fields
  if (HIDDEN_FIELDS.includes(key)) return false;

  // Skip null/undefined values
  if (value === null || value === undefined) return false;

  // Skip relationship fields that are not "many" type
  if (fieldDef?.display_type === RelationshipFieldDisplayType.RELATIONSHIP) {
    // Show only if it's an array or has a __count property (indicating many relationship)
    return (
      Array.isArray(value) ||
      (typeof value === "object" && value?.__count !== undefined)
    );
  }

  return true;
};

interface SimpleKanbanBoardProps {
  entityDefinition: IEntity;
  entityData: PaginatedResult<IAny> | null;
  isLoading: boolean;
}

interface BoardType {
  columns: LibKanbanColumn[];
}

const SimpleKanbanBoard: React.FC<SimpleKanbanBoardProps> = ({
  entityDefinition,
  entityData,
  isLoading,
}) => {
  const queryClient = useQueryClient();
  const { currentTenant, safeNavigate } = useAuth();
  const [board, setBoard] = useState<BoardType>({ columns: [] });
  const [statusField, setStatusField] = useState<string>("");
  const [renderKey, setRenderKey] = useState(0);

  // Initialize status field from storage or find first select field
  useEffect(() => {
    const storedField = getStoredStatusField(
      entityDefinition.name,
      currentTenant?.id || null,
    );
    if (
      storedField &&
      entityDefinition.fields.some((field) => field.name === storedField)
    ) {
      setStatusField(storedField);
    } else {
      const selectField = entityDefinition.fields.find(
        (field) => field.display_type === StandardFieldDisplayType.SELECT,
      );
      if (selectField) {
        setStatusField(selectField.name);
        saveStatusFieldForEntity(
          entityDefinition.name,
          currentTenant?.id || null,
          selectField.name,
        );
      }
    }
  }, [entityDefinition, currentTenant?.id]);

  // Save status field selection when it changes
  const handleStatusFieldChange = (newField: string) => {
    setStatusField(newField);
    saveStatusFieldForEntity(
      entityDefinition.name,
      currentTenant?.id || null,
      newField,
    );
  };

  // Update render key when status field changes
  useEffect(() => {
    setRenderKey((prev) => prev + 1);
  }, [statusField]);

  // Generate board data when items or status field changes
  useEffect(() => {
    if (!statusField || !entityData?.items) {
      return;
    }

    const field = entityDefinition.fields.find((f) => f.name === statusField);

    if (!field?.options?.select?.options) {
      return;
    }

    // Create columns from select options
    const columns = field.options.select.options.map((option) => ({
      id: option.value,
      title: option.label,
      cards: [] as LibKanbanCard[],
    }));

    // Distribute items into columns
    entityData.items.forEach((item, index) => {
      const status = item[statusField];
      const column = columns.find((col) => col.id === status);

      if (column) {
        const card: LibKanbanCard = {
          id: String(item.id),
          title: String(item.title || `Item ${item.id}`),
          description: item.description ? String(item.description) : "",
          column_id: column.id,
          position: index,
          entityName: entityDefinition.name,
          [statusField]: status,
          rawData: item,
        };
        column.cards.push(card);
      }
    });

    const newBoard = { columns };
    setBoard(newBoard);
  }, [entityData, statusField, entityDefinition]);

  const handleCardMove = async (
    card: LibKanbanCard,
    _: { fromColumnId: string; fromPosition: number },
    destination: { toColumnId: string; toPosition: number },
  ) => {
    try {
      const cardId = parseInt(String(card.id), 10);
      if (isNaN(cardId)) {
        console.error("Invalid card ID:", card.id);
        return false;
      }

      await anyService.updateAny(entityDefinition.name, cardId, {
        [statusField]: destination.toColumnId,
      });

      await queryClient.invalidateQueries({
        queryKey: ["entities", entityDefinition.name],
      });

      return true;
    } catch (error) {
      console.error("Failed to update card status:", error);
      return false;
    }
  };

  const renderCard = (
    card: Record<string, any>,
    { dragging }: { dragging: boolean },
  ) => {
    // Get the title from the field marked as name field
    const nameField =
      entityDefinition.fields.find((field) => field.is_name_field)?.name ||
      "title";
    const title = card.rawData?.[nameField]
      ? String(card.rawData[nameField])
      : "Untitled";

    // Get field definitions for the raw data
    const getFieldDefinition = (fieldName: string) => {
      return entityDefinition.fields.find((field) => field.name === fieldName);
    };

    return (
      <div
        className={`w-[300px] min-w-[300px] max-w-[300px] bg-white dark:bg-gray-700 rounded-lg shadow-sm mb-4 cursor-pointer hover:shadow-md transition-shadow ${
          dragging ? "rotate-2 shadow-lg" : ""
        }`}
        onClick={() =>
          safeNavigate(
            `/org/${currentTenant?.id}/entities/${entityDefinition.name}/${card.id}`,
          )
        }
      >
        <div className="p-4 w-full">
          {/* Title */}
          <div className="font-medium text-lg text-left wrap dark:text-white">
            {title}
          </div>

          {/* Additional fields with proper rendering */}
          {card.rawData && typeof card.rawData === "object" && (
            <div className="mt-3 space-y-2">
              {Object.entries(card.rawData as Record<string, any>)
                .filter(([key, value]) => {
                  const fieldDef = getFieldDefinition(key);
                  return shouldShowField(key, value, fieldDef);
                })
                .map(([key, value]) => {
                  const fieldDef = getFieldDefinition(key);
                  const displayValue = formatFieldValue(
                    value,
                    fieldDef?.display_type,
                  );

                  return (
                    <div
                      key={key}
                      className="flex justify-between items-center"
                    >
                      <span className="text-xs text-gray-500 dark:text-gray-400 text-left capitalize">
                        {key.replace(/_/g, " ")}:
                      </span>
                      <div
                        className="text-sm text-right truncate max-w-[180px]"
                        title={displayValue}
                      >
                        {displayValue}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}

          {/* Timestamps */}
          {(card.rawData.created_at || card.rawData.updated_at) && (
            <div className="mt-3 text-xs text-gray-500">
              {card.rawData.updated_at ? (
                <div className="text-blue-500 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                  Updated: {formatDate(card.rawData.updated_at)}
                </div>
              ) : card.rawData.created_at ? (
                <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded">
                  Created: {formatDate(card.rawData.created_at)}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderColumnHeader = (column: LibKanbanColumn) => (
    <div className="p-2 mb-2 font-medium text-gray-700 dark:text-gray-200">
      {column.title}
      <div className="text-sm text-gray-500 dark:text-gray-400">
        {column.cards.length} items
      </div>
    </div>
  );

  if (isLoading) {
    return <LoadingScreen fullPage={false} />;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-shrink-0 flex items-center gap-3 px-3 py-2">
        <label
          htmlFor="status-field"
          className="text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          Status Field:
        </label>
        <select
          id="status-field"
          value={statusField}
          onChange={(e) => handleStatusFieldChange(e.target.value)}
          className="block w-48 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        >
          {entityDefinition.fields
            .filter(
              (field) => field.display_type === StandardFieldDisplayType.SELECT,
            )
            .map((field) => (
              <option key={field.name} value={field.name}>
                {field.name}
              </option>
            ))}
        </select>
      </div>

      {!board || !board.columns || board.columns.length === 0 ? (
        <div className="flex-grow flex items-center justify-center p-8 bg-gray-50 dark:bg-gray-800">
          <p className="text-gray-500 dark:text-gray-400">
            No columns available for the selected status field.
          </p>
        </div>
      ) : (
        <div className="flex-1 overflow-hidden">
          <div className="h-full w-full overflow-x-auto">
            <UncontrolledBoard
              key={`board-${statusField}-${renderKey}`}
              initialBoard={board}
              onCardDragEnd={handleCardMove}
              renderCard={renderCard}
              renderColumnHeader={renderColumnHeader}
              onColumnDragEnd={() => false}
              disableColumnDrag
            />
          </div>
        </div>
      )}

      <style>{`
        .react-kanban-board {
          display: flex;
          padding: 1rem;
          align-items: stretch;
          gap: 1.5rem;
          min-width: min-content;
          height: calc(100% - 2rem);
          width: max-content;
        }

        .react-kanban-column {
          background: #f8f9fa;
          border-radius: 0.5rem;
          width: 332px;
          min-width: 332px;
          max-width: 332px;
          margin: 0;
          display: flex;
          flex-direction: column;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          margin-right: 1rem;
        }

        .dark .react-kanban-column {
          background: #1f2937;
        }

        .react-kanban-column-header {
          padding: 0.75rem;
          background: transparent;
          flex-shrink: 0;
          border-bottom: 1px solid #e5e7eb;
        }

        .dark .react-kanban-column-header {
          border-bottom-color: #374151;
        }

        .react-kanban-column-body {
          padding: 0.75rem;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          overflow-y: auto;
          height: 100%;
          min-height: 0;
        }

        /* Scrollbar styles for column body */
        .react-kanban-column-body::-webkit-scrollbar {
          width: 8px;
        }

        .react-kanban-column-body::-webkit-scrollbar-track {
          background: transparent;
          margin: 0.25rem;
        }

        .react-kanban-column-body::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 999px;
          border: 2px solid transparent;
          background-clip: padding-box;
        }

        .react-kanban-column-body::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
          border: 2px solid transparent;
          background-clip: padding-box;
        }

        .dark .react-kanban-column-body::-webkit-scrollbar-thumb {
          background: #4b5563;
          border: 2px solid transparent;
          background-clip: padding-box;
        }

        .dark .react-kanban-column-body::-webkit-scrollbar-thumb:hover {
          background: #6b7280;
          border: 2px solid transparent;
          background-clip: padding-box;
        }

        /* Card styles */
        .react-kanban-card {
          background: white;
          border-radius: 0.375rem;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          margin: 0;
          width: 332px;
          min-width: 332px;
          max-width: 332px;
        }

        .dark .react-kanban-card {
          background-color: #374151;
          color: #e5e7eb;
        }

        .react-kanban-card--dragging {
          transform: rotate(2deg);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .dark .react-kanban-card--dragging {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        /* Main scrollbar styles (horizontal scroll) */
        .overflow-x-auto::-webkit-scrollbar {
          height: 12px;
        }

        .overflow-x-auto::-webkit-scrollbar-track {
          background: transparent;
        }

        .overflow-x-auto::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 999px;
          border: 3px solid transparent;
          background-clip: padding-box;
        }

        .overflow-x-auto::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
          border: 3px solid transparent;
          background-clip: padding-box;
        }

        .dark .overflow-x-auto::-webkit-scrollbar-thumb {
          background: #4b5563;
          border: 3px solid transparent;
          background-clip: padding-box;
        }

        .dark .overflow-x-auto::-webkit-scrollbar-thumb:hover {
          background: #6b7280;
          border: 3px solid transparent;
          background-clip: padding-box;
        }
      `}</style>
    </div>
  );
};

export default SimpleKanbanBoard;
